/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import objectHash from "object-hash";

import {
  AtLeast,
  BlockSnapshot,
  CreateTransaction,
  DeleteTransaction,
  Transaction,
  TransactionType,
  UpdateTransaction,
} from "@vienna/common";

import { TransactionInfo } from "./types";

export class TransactionManager {
  private plateId: string;

  private userId: string;

  public rollingTransaction: UpdateTransaction;

  /**
   * Finalized transactions that will be sent to the server
   */
  public ledger: Map<string, Transaction> = new Map();

  constructor(plateId: string, userId: string) {
    this.plateId = plateId;

    this.userId = userId;
    this.rollingTransaction = this.initializeRollingTransaction();
  }

  /* -------------------------------------------------- */
  /*                  Create & Delete                   */
  /* -------------------------------------------------- */
  public makeCreateTransaction(blocks: BlockSnapshot[]): CreateTransaction {
    return {
      type: TransactionType.Create,
      blocks,
      plateId: this.plateId,
      userId: this.userId,
      hash: objectHash({ blocks }),
      size: JSON.stringify({ blocks }).length,
      time: new Date(),
    };
  }

  public makeDeleteTransaction(blockIds: string[]): DeleteTransaction {
    return {
      type: TransactionType.Delete,
      blockIds: blockIds,
      plateId: this.plateId,
      userId: this.userId,
      hash: objectHash({ blockIds }),
      size: JSON.stringify({ blockIds }).length,
      time: new Date(),
    };
  }

  /* -------------------------------------------------- */
  /*                Rolling Transaction                 */
  /* -------------------------------------------------- */
  private initializeRollingTransaction(blocks: BlockSnapshot[] = []): UpdateTransaction {
    return {
      type: TransactionType.Update,
      blocks,
      plateId: this.plateId,
      userId: this.userId,
      hash: "",
      size: 0,
      time: new Date(),
    };
  }

  public updateRollingTransaction(updatedBlocks: BlockSnapshot[]): void {
    const oldBlocks = this.rollingTransaction.blocks.filter((b) => !updatedBlocks.find((nb) => nb.id === b.id));
    const newBlocks = [...oldBlocks, ...updatedBlocks];
    this.rollingTransaction = {
      ...this.rollingTransaction,
      blocks: newBlocks,
      time: new Date(),
      hash: objectHash({ blocks: newBlocks }),
      size: JSON.stringify({ blocks: newBlocks }).length,
    };
  }

  public resetRollingTransaction(): void {
    this.rollingTransaction = this.initializeRollingTransaction();
  }

  /* -------------------------------------------------- */
  /*                        Misc.                       */
  /* -------------------------------------------------- */
  public getInfo(transaction: Transaction): TransactionInfo {
    const data = transaction.type === TransactionType.Delete ? transaction.blockIds : transaction.blocks;
    return {
      size: JSON.stringify(data).length,
      time: this.ledger.get(transaction.hash)!.time,
      hash: transaction.hash,
    };
  }

  /**
   * Commit a transaction to the ledger, will be sent to the server
   */
  public commit(transaction: Transaction): void {
    this.ledger.set(transaction.hash, transaction);
  }

  public removeFromLedger(hash: string): void {
    this.ledger.delete(hash);
  }

  /**
   * Check if a transaction is updating a block that hasn't been confirmed as created yet
   */
  public canSendUpdate(): boolean {
    return this.rollingTransaction.blocks.every((block) => !this.blockIsInLedger(block));
  }

  private blockIsInLedger(block: AtLeast<BlockSnapshot, "id">): boolean {
    // @todo do we need to check for delete transactions?
    return Array.from(this.ledger.values()).some(
      (transaction) =>
        transaction.type === TransactionType.Create && transaction.blocks.some((b) => b.id === block.id)
    );
  }
}
